import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { UpdateCard } from '../components/UpdateCard';
import { EmptyState, ErrorMessage, LoadingGrid } from '../components/LoadingGrid';
import { api } from '../lib/api-client';

/**
 * Jelajahi — pintu masuk ke pencarian, seluruh platform, dan yang sedang ramai.
 *
 * Catatan: referensi desain memakai grid GENRE. Backend QuickPlay belum
 * menyediakan endpoint telusur per-genre (genre hanya muncul di detail judul),
 * jadi sumbu jelajah di sini adalah PLATFORM — sumbu yang memang nyata di app
 * ini — dengan bentuk grid yang sama.
 */
export function EksplorPage() {
  const navigate = useNavigate();

  const platformsQuery = useQuery({
    queryKey: ['platforms'],
    queryFn: () => api.platforms(),
    staleTime: Infinity,
  });

  const healthQuery = useQuery({
    queryKey: ['platform-health'],
    queryFn: () => api.platformHealth(),
    staleTime: 10 * 60 * 1000,
  });
  const maintenance = new Set(
    (healthQuery.data?.items ?? [])
      .filter((h) => h.status === 'maintenance')
      .map((h) => h.platform),
  );

  const popularQuery = useQuery({
    queryKey: ['updates', 'populer'],
    queryFn: () => api.updates({ limit: 12 }),
    staleTime: 5 * 60 * 1000,
  });

  const platforms = [...(platformsQuery.data?.items ?? [])].sort((a, b) => {
    const am = maintenance.has(a.id) ? 1 : 0;
    const bm = maintenance.has(b.id) ? 1 : 0;
    return am - bm;
  });

  return (
    <div className="mx-auto max-w-3xl px-4 safe-top">
      <h1 className="py-3 font-display text-2xl font-extrabold">Jelajahi</h1>

      {/* Pencarian */}
      <Link
        to="/search"
        className="flex min-h-[48px] items-center gap-3 rounded-2xl border border-line/[0.07] bg-ink-850 px-4"
      >
        <svg className="text-neutral-500" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
          <circle cx="11" cy="11" r="7.5" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <span className="text-sm text-neutral-500">Cari donghua atau drama&hellip;</span>
      </Link>

      {/* Platform */}
      <section className="mt-6">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="font-display text-base font-bold">Platform</h2>
          <span className="text-[11px] text-neutral-500">
            {platforms.length} sumber
          </span>
        </div>

        {platformsQuery.isPending ? (
          <div className="grid grid-cols-4 gap-2.5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-[72px] animate-pulse rounded-2xl bg-ink-900" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2.5">
            {platforms.map((p) => {
              const isMaint = maintenance.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => navigate(`/search?platform=${encodeURIComponent(p.id)}`)}
                  title={isMaint ? 'Platform sedang bermasalah / maintenance' : undefined}
                  className={`relative flex min-h-[72px] flex-col items-center justify-center gap-2 rounded-2xl border border-line/[0.06] bg-ink-900 px-1 transition active:scale-95 ${
                    isMaint ? 'opacity-60' : ''
                  }`}
                >
                  <span className="grid h-7 w-7 place-items-center rounded-lg bg-brand-500/12 font-display text-[11px] font-extrabold uppercase text-brand-500">
                    {p.displayName.slice(0, 2)}
                  </span>
                  <span className="line-clamp-1 w-full px-1 text-center text-[10px] font-semibold text-neutral-300">
                    {p.displayName}
                  </span>
                  {isMaint && (
                    <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-ink-900" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Populer */}
      <section className="mt-7 pb-4">
        <h2 className="mb-3 font-display text-base font-bold">Populer Minggu Ini</h2>
        {popularQuery.isPending ? (
          <LoadingGrid />
        ) : popularQuery.isError ? (
          <ErrorMessage
            title="Gagal memuat"
            message={popularQuery.error.message}
            onRetry={() => void popularQuery.refetch()}
          />
        ) : popularQuery.data.items.length === 0 ? (
          <EmptyState message="Belum ada data." />
        ) : (
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {popularQuery.data.items.map((u) => (
              <UpdateCard key={`${u.platform}-${u.dramaId}`} update={u} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
